import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Alert,
  Stack,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemText,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Modal,
  CircularProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import {
  FormatItalic,
  FormatBold,
  StrikethroughS,
  Code,
  FormatQuote,
  FormatListBulleted,
  FormatListNumbered,
} from "@mui/icons-material";
import PersonIcon from "@mui/icons-material/Person";
import ChatIcon from "@mui/icons-material/Chat";
import { useChatContext } from "../hooks/ChatProvider";
import { blue } from "@mui/material/colors";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import EmojiPicker from "emoji-picker-react";
import timezone from "dayjs/plugin/timezone";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import TranslateIcon from "@mui/icons-material/Translate";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import {
  CHAT_START_TOPIC_ENDPOINT,
  CHAT_END_TOPIC_ENDPOINT,
} from "../config/urls";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { useAuth } from "../hooks/AuthProvider";
import CustomMarkdownEditor from "./Whatsapp/CustomMarkdownEditor";
import { ArrowLeftIcon } from "@mui/x-date-pickers";
import ChatMessageRow from "./ChatMessageRow";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { ColorsConstants } from "../cssUtils/constants";
import { getTenantTimezone } from "../utils/dateUtils";
import { WS_BASE_URL } from "../config/environment";

dayjs.extend(utc);
dayjs.extend(timezone);

// ---------- helpers ----------
const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  return parts.length >= 2
    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
};

const formatDateLabel = (timestamp) => {
  const tz = getTenantTimezone();
  const d = dayjs(timestamp).tz(tz);
  const today = dayjs().tz(tz);
  if (d.isSame(today, "day")) return "Today";
  if (d.isSame(today.subtract(1, "day"), "day")) return "Yesterday";
  return d.format("MMM D, YYYY");
};

// Group messages by date
const groupByDate = (messages) => {
  const groups = [];
  let lastDate = null;
  messages.forEach((msg) => {
    const dateLabel = formatDateLabel(msg.timestamp);
    if (dateLabel !== lastDate) {
      groups.push({
        type: "date",
        label: dateLabel,
        id: `date-${msg.timestamp}`,
      });
      lastDate = dateLabel;
    }
    groups.push({ type: "message", ...msg });
  });
  return groups;
};

const ChatWindow = ({ setIsChatSelected }) => {
  const { selectedUser, setSelectedUser, setChatData } = useChatContext();
  console.log(selectedUser, "Selected user")
  const [data, setData] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isEndChatModalOpen, setIsEndChatModalOpen] = useState(false);
  const [selectedTone, setSelectedTone] = useState("");
  const { getValidToken } = useAuth();
  const socket = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [responseText, setResponseText] = useState("");
  const [totalCreditLeft, setTotalCreditLeft] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [messagesPage, setMessagesPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const openAIprompt = Boolean(anchorEl);
  const prevTopicRef = useRef(null);
  const messagesCacheRef = useRef(new Map());
  const URL =
    process.env.NODE_ENV === "production" ? "api" : "http://localhost:8000/api";
  const NEWURL =
    process.env.NODE_ENV === "production" ? "" : "http://localhost:8000";
  const adminSubscriberCode = localStorage.getItem("code");
  const tenant = localStorage.getItem("tenant");
  const [hideInputEditor, setHideInputEditor] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const chatContainerRef = useRef(null);
  const permissions = localStorage.getItem("permissions") || [];
  const generativeAiReadPermission = "gen_ai_live_agent.view";
  const generativeAiAccessPermission = "gen_ai_live_agent.add";
  const hasReadPermission = permissions.includes(generativeAiReadPermission);
  const hasAccessPermission = permissions.includes(
    generativeAiAccessPermission,
  );
  const canAccessAI = hasReadPermission && hasAccessPermission;

  const onEmojiClick = (emojiObject) =>
    setInputValue((prev) => prev + emojiObject.emoji);
  const handleMenuClose = () => setAnchorEl(null);
  const handleInsert = () => {
    setInputValue(responseText);
    setModalOpen(false);
  };
  const handleAIPromptClick = (event) => setAnchorEl(event.currentTarget);
  const handleAIPromptClose = () => setAnchorEl(null);
  const handleSubmenuOpen = (event) => setSubmenuAnchorEl(event.currentTarget);
  const handleSubmenuClose = () => setSubmenuAnchorEl(null);

  const handleMenuItemClick = async (tone) => {
    try {
      const requestUrl = `${URL}/transform-text/`;
      const token = await getValidToken();
      const response = await fetch(requestUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ prompt: inputValue, tone: tone.toLowerCase() }),
      });
      const d = await response.json();
      setResponseText(d.enhanced_text || "");
      setSelectedTone(tone);
      setTotalCreditLeft(d.remaining_tokens);
      setModalOpen(true);
    } catch (error) {
      console.error("Error calling the API:", error);
    } finally {
      handleMenuClose();
      handleSubmenuClose();
    }
  };

  useEffect(() => {
    setHideInputEditor(!selectedUser?.is_active);
  }, [selectedUser]);

  useEffect(() => {
    if (socket.current) socket.current.close();
    if (selectedUser) {
      // setData([]);
      // setIsMessagesLoading(true);
      // setMessagesPage(1);
      // setHasMoreMessages(false);
      // setIsLoadingMore(false);

      const topicChanged = prevTopicRef.current !== selectedUser.topic;
      prevTopicRef.current = selectedUser.topic;
      const currentTopic = selectedUser.topic;
      const cachedTopicState = messagesCacheRef.current.get(currentTopic);

      if (topicChanged) {
        if (cachedTopicState?.messages?.length) {
          // Instant render from memory cache while websocket refreshes in background.
          setData(cachedTopicState.messages);
          setHasMoreMessages(Boolean(cachedTopicState.hasMoreMessages));
          setIsMessagesLoading(false);
        } else {
          setData([]);
          setIsMessagesLoading(true);
        }
        setMessagesPage(1);
        if (!cachedTopicState?.messages?.length) setHasMoreMessages(false);
        setIsLoadingMore(false);
      }
      const socketURL = `${WS_BASE_URL}/ws/chat/${currentTopic}/?sub=${adminSubscriberCode}&tenant=${tenant}`;
      socket.current = new WebSocket(socketURL);
      socket.current.onopen = () => console.log("WS opened");
      socket.current.onmessage = (event) => {
        if (!event?.data) return;

        const message = JSON.parse(event.data);
        console.log("WS Message", message);
        const currentTenant = localStorage.getItem("tenant");
        if (message.type === "messages.init") {
          const filteredMessages = message.messages.filter(
            (m) => m.tenant_subdomain === currentTenant,
          );
          setData(filteredMessages);
          messagesCacheRef.current.set(currentTopic, {
            messages: filteredMessages,
            hasMoreMessages: message.has_more,
          });
          setIsMessagesLoading(false);
          setHasMoreMessages(message.has_more);
          setMessagesPage(1);
          return;
        }
        if (message.type === "messages.page") {
          const filteredMessages = message.messages.filter(
            (m) => m.tenant_subdomain === currentTenant,
          );
          setData((prev) => {
            const merged = [...filteredMessages, ...prev];
            messagesCacheRef.current.set(currentTopic, {
              messages: merged,
              hasMoreMessages: message.has_more,
            });
            return merged;
          });
          setHasMoreMessages(message.has_more);
          setIsLoadingMore(false);
          return;
        }
        if (message.tenant_subdomain === currentTenant)
          setData((prev) => {
            const exists = prev.some(
              (m) =>
                m.timestamp === message.timestamp &&
                m.message === message.message
            );

            if (exists) return prev;

            const updatedMessages = [...prev, message];

            messagesCacheRef.current.set(currentTopic, {
              messages: updatedMessages,
              hasMoreMessages,
            });

            return updatedMessages;
          });
      };
      socket.current.onerror = () => console.error("WS error");
      socket.current.onclose = () => console.log("WS closed");
    }
    return () => {
      if (socket.current) socket.current.close();
    };
  }, [selectedUser?.topic]);

  const handleOnClick = () => {
    if (inputValue.trim() && socket.current?.readyState === WebSocket.OPEN) {
      const newMessage = {
        user: adminSubscriberCode,
        message: inputValue,
        timestamp: new Date().toISOString(),
        utype: "SPT",
      };
      try {
        socket.current.send(JSON.stringify(newMessage));
        setInputValue("");
      } catch (e) {
        console.error("Send error:", e);
      }
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleOnClick();
    }
  };

  const openEndChatModal = () => setIsEndChatModalOpen(true);
  const closeEndChatModal = () => setIsEndChatModalOpen(false);

  const handleStartChat = async () => {
    try {
      const response = await fetch(
        `${CHAT_START_TOPIC_ENDPOINT}${selectedUser.topic}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
          body: JSON.stringify({ code: adminSubscriberCode }),
        },
      );
      if (response.ok) {
        console.log("Chat started");
        setChatData(null);
        setSelectedUser((prev) =>
          prev
            ? {
                ...prev,
                supportUser: { code: adminSubscriberCode },
                is_active: true,
              }
            : prev,
        );
      }
    } catch (e) {
      console.error("Error starting chat:", e);
    }
  };

  const confirmEndChat = async () => {
    try {
      const response = await fetch(
        `${CHAT_END_TOPIC_ENDPOINT}${selectedUser.topic}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
          body: JSON.stringify({ is_active: false }),
        },
      );
      if (response.ok) {
        // Keep current chat selected/visible after ending; only update list state.
        window.dispatchEvent(
          new CustomEvent("bb-after-end-chat", {
            detail: { endedTopic: selectedUser.topic },
          }),
        );
      }
    } catch (e) {
      console.error("Error ending chat:", e);
    } finally {
      closeEndChatModal();
    }
  };

  const prevDataLengthRef = useRef(0);
  useEffect(() => {
    if (!isLoadingMore && chatContainerRef.current)
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
  }, [data.length === 0]);

  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;
    const wasAtBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <
      100;
    if (wasAtBottom || prevDataLengthRef.current === 0)
      container.scrollTop = container.scrollHeight;
    prevDataLengthRef.current = data.length;
  }, [data]);

  const handleChatScroll = () => {
    const container = chatContainerRef.current;
    if (!container) return;
    if (container.scrollTop === 0 && hasMoreMessages && !isLoadingMore) {
      if (!socket.current || socket.current.readyState !== WebSocket.OPEN) {
        return;
      }
      setIsLoadingMore(true);
      const nextPage = messagesPage + 1;
      setMessagesPage(nextPage);
      const prevScrollHeight = container.scrollHeight;
      socket.current.send(
        JSON.stringify({ type: "messages.load_more", page: nextPage }),
      );
      setTimeout(() => {
        container.scrollTop = container.scrollHeight - prevScrollHeight;
      }, 100);
    }
  };

  const grouped = groupByDate(data);
  const isInputDisabled =
    !selectedUser ||
    !selectedUser.supportUser ||
    selectedUser.supportUser.code !== adminSubscriberCode;

  // Empty state
  if (!selectedUser) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        sx={{ bgcolor: "#ece5dd" }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            bgcolor: "#e8f0fe",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <ChatIcon sx={{ color: "#1976d2", fontSize: 32 }} />
        </Box>
        <Typography variant="h6" fontWeight={600} color="#333" mb={0.5}>
          Select a conversation
        </Typography>
        <Typography variant="body2" color="#aaa">
          Choose a chat from the list to get started
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      width="100%"
      sx={{ bgcolor: "#ece5dd", maxWidth: "100%" }}
    >
      {/* Header */}
      <Box
        sx={{
          px: { xs: 1.5, md: 2.5 },
          py: { xs: 1, md: 1.25 },
          borderBottom: "1px solid #e8e8e8",
          display: "flex",
          alignItems: "center",
          bgcolor: "#ffffff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          minHeight: 64,
        }}
      >
        {isMobile && (
          <IconButton
            size="small"
            onClick={() => setIsChatSelected(false)}
            sx={{ mr: 0.5 }}
          >
            <ArrowLeftIcon fontSize="small" />
          </IconButton>
        )}
        <Avatar
          sx={{
            width: { xs: 36, md: 42 },
            height: { xs: 36, md: 42 },
            bgcolor: "#1976d2",
            fontSize: { xs: "0.8rem", md: "0.95rem" },
            fontWeight: 700,
            mr: { xs: 1, md: 1.5 },
          }}
        >
          {getInitials(selectedUser?.name)}
        </Avatar>
        <Box flex={1} minWidth={0}>
          <Typography
            fontWeight={700}
            fontSize={{ xs: "0.9rem", md: "1rem" }}
            color="#1a1a2e"
            noWrap
          >
            {selectedUser?.name || "User"}
          </Typography>
          <Typography fontSize="0.75rem" color="#888" noWrap>
            +{selectedUser?.phone}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 0.5,
              color: "#aaa",
            }}
          >
            <AccessTimeIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" color="#aaa">
              Chats may take up to 2 minutes to sync
            </Typography>
          </Box>

          {!hideInputEditor && (
            <>
              {selectedUser?.supportUser ? (
                <Button
                  variant="contained"
                  color="error"
                  onClick={openEndChatModal}
                  startIcon={<ChatIcon sx={{ fontSize: "1rem !important" }} />}
                  disabled={
                    selectedUser.supportUser.code !== adminSubscriberCode
                  }
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: { xs: "0.75rem", md: "0.85rem" },
                    px: { xs: 1.5, md: 2 },
                    py: 0.75,
                    boxShadow: "none",
                    "&:hover": { boxShadow: "none" },
                  }}
                >
                  {isMobile ? "End" : "End Chat"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleStartChat}
                  startIcon={<ChatIcon sx={{ fontSize: "1rem !important" }} />}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: { xs: "0.75rem", md: "0.85rem" },
                    px: { xs: 1.5, md: 2 },
                    py: 0.75,
                    bgcolor: "#1976d2",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#1565c0", boxShadow: "none" },
                  }}
                >
                  {isMobile ? "Start" : "Start Chat"}
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Messages Area — single Box, no duplicate */}
      <Box
        ref={chatContainerRef}
        flex={1}
        overflow="auto"
        onScroll={handleChatScroll}
        sx={{
          px: { xs: 1.5, md: 3 },
          py: 2,
          scrollbarWidth: "thin",
          scrollbarColor: "#e0e0e0 transparent",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": {
            background: "#e0e0e0",
            borderRadius: 4,
          },
        }}
      >
        {isMessagesLoading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            gap={1.5}
          >
            <CircularProgress size={32} sx={{ color: ColorsConstants.primaryBackgroundColor }} />
            <Typography variant="caption" color="#aaa">
              Loading messages...
            </Typography>
          </Box>
        ) : (
          <>
            {isLoadingMore && (
              <Typography
                variant="caption"
                display="block"
                textAlign="center"
                color="#aaa"
                mb={2}
              >
                Loading older messages...
              </Typography>
            )}

            {grouped.length === 0 && !isLoadingMore && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                gap={1.5}
                sx={{ userSelect: "none" }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.7)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                  }}
                >
                  <ChatIcon sx={{ fontSize: 28, color: "#b0bec5" }} />
                </Box>
                <Typography
                  sx={{ fontWeight: 600, fontSize: "0.92rem", color: "#808080" }}
                >
                  No messages yet
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    color: "#78909c",
                    textAlign: "center",
                    maxWidth: 220,
                    lineHeight: 1.5,
                  }}
                >
                  Messages from this conversation will appear here.
                </Typography>
              </Box>
            )}

            {grouped.map((item, index) => {
              if (item.type === "date") {
                return (
                  <Box
                    key={item.id}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    my={2}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 0.4,
                        bgcolor: "#e8e8e8",
                        borderRadius: 12,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          color: "#666",
                          fontWeight: 500,
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                  </Box>
                );
              }

              return <ChatMessageRow key={index} msg={item} />;
            })}
          </>
        )}
      </Box>

      {/* Input Area */}
      {!hideInputEditor && (
        <Box
          sx={{
            borderTop: "1px solid #e8e8e8",
            bgcolor: "#ffffff",
            px: { xs: 1.5, md: 2.5 },
            pt: 1,
            pb: { xs: 1, md: 1.5 },
          }}
        >
          <Box display="flex" alignItems="flex-end" gap={1}>
            <Box sx={{ flex: 1 }}>
              <CustomMarkdownEditor
                value={inputValue}
                onChange={(value) => setInputValue(value)}
                onKeyDown={handleKeyDown}
                maxLength={256}
                disabled={isInputDisabled}
              />
            </Box>
            <IconButton
              onClick={handleOnClick}
              disabled={!selectedUser || !inputValue.trim()}
              sx={{
                width: 44,
                height: 44,
                bgcolor: inputValue.trim() ? "#1976d2" : "#e0e0e0",
                color: "#ffffff",
                borderRadius: "12px",
                transition: "all 0.2s ease",
                flexShrink: 0,
                mb: 0.5,
                "&:hover": {
                  bgcolor: inputValue.trim() ? "#1565c0" : "#e0e0e0",
                },
                "&.Mui-disabled": { bgcolor: "#e0e0e0", color: "#bbb" },
              }}
            >
              <SendIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Emoji Picker */}
      {showEmojiPicker && (
        <Box position="absolute" bottom="80px" right="20px" zIndex={1300}>
          <EmojiPicker
            reactionsDefaultOpen={true}
            onReactionClick={onEmojiClick}
            onEmojiClick={onEmojiClick}
          />
        </Box>
      )}

      {/* End Chat Dialog */}
      <Dialog
        open={isEndChatModalOpen}
        onClose={closeEndChatModal}
        PaperProps={{ sx: { borderRadius: "12px", p: 0.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>End Chat</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to end the chat with{" "}
            <strong>
              {selectedUser ? `+${selectedUser.phone}` : "this user"}
            </strong>
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 2.5 }}>
          <Button
            onClick={closeEndChatModal}
            variant="outlined"
            sx={{ borderRadius: "8px", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmEndChat}
            variant="contained"
            color="error"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              boxShadow: "none",
            }}
          >
            End Chat
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Transform Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", md: 600 },
            bgcolor: "background.paper",
            borderRadius: "16px",
            boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
            p: 3,
          }}
        >
          <Typography variant="h6" fontWeight={700} mb={1.5}>
            Transformed Text
          </Typography>
          <Alert
            severity="info"
            sx={{ mb: 2, borderRadius: "8px", fontSize: "0.8rem" }}
          >
            {`${totalCreditLeft} credits remaining. Contact admin to get more.`}
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
          <Button
            variant="contained"
            onClick={handleInsert}
            sx={{
              mt: 2,
              borderRadius: "8px",
              textTransform: "none",
              boxShadow: "none",
            }}
          >
            Insert
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default ChatWindow;
